import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { REPLAY_RESULT_VARIANT } from '@/lib/api/replays.constants';

import type { ScenarioJudgeOutput } from '@/lib/api/scenario-runs.api';

type CheckResult = ScenarioJudgeOutput['checks'][number]['result'];

/** Not applicable is neutral: the situation the check concerns never arose. */
const CHECK_RESULT_VARIANT: Record<
  CheckResult,
  'default' | 'secondary' | 'destructive' | 'outline'
> = { ...REPLAY_RESULT_VARIANT, not_applicable: 'outline' };

/** Lists a scenario's rules and checks with the judge's result for each. */
export const ExpectationsPanel = ({ output }: { output: ScenarioJudgeOutput }) => {
  const rules = output.rules ?? [];
  const checks = output.checks ?? [];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">
          Expectations{output.scenarioName ? ` — ${output.scenarioName}` : ''}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Badge variant={REPLAY_RESULT_VARIANT[output.verdict.result]}>
            {output.verdict.result}
          </Badge>
          {output.endedBy && (
            <span className="text-muted-foreground text-xs">ended by: {output.endedBy}</span>
          )}
        </div>
        {output.verdict.reasons.length > 0 && (
          <ul className="text-muted-foreground list-inside list-disc space-y-1 text-sm">
            {output.verdict.reasons.map((reason, i) => (
              <li key={i}>{reason}</li>
            ))}
          </ul>
        )}

        {rules.length > 0 && (
          <div>
            <p className="text-muted-foreground mb-1 text-xs font-medium uppercase">Rules</p>
            <ul className="space-y-1.5">
              {rules.map((rule, i) => (
                <li key={i} className="flex flex-wrap items-baseline gap-2 text-sm">
                  <Badge variant={REPLAY_RESULT_VARIANT[rule.result]} className="capitalize">
                    {rule.result}
                  </Badge>
                  <span className="font-mono text-xs">{rule.rule}</span>
                  <span className="text-muted-foreground text-xs">{rule.detail}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {checks.length > 0 && (
          <div>
            <p className="text-muted-foreground mb-1 text-xs font-medium uppercase">Checks</p>
            <ul className="space-y-3">
              {checks.map((check) => (
                <li key={check.id} className="space-y-1 text-sm">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <Badge variant={CHECK_RESULT_VARIANT[check.result]} className="capitalize">
                      {check.result === 'not_applicable' ? 'not applicable' : check.result}
                    </Badge>
                    <span>{check.text}</span>
                    {check.applicability === 'conditional' && (
                      <span className="text-muted-foreground text-xs">(optional branch)</span>
                    )}
                  </div>
                  {check.reason && <p className="text-muted-foreground text-xs">{check.reason}</p>}
                  {check.evidence && (
                    <blockquote className="text-muted-foreground border-l-2 pl-3 text-xs italic">
                      {check.evidence}
                    </blockquote>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
